import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiFieldNumber, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiHorizontalRule, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiSwitch, EuiText, EuiTextArea, EuiTitle } from '@elastic/eui'

import { Account } from 'api/interfaces'
import { useBeginCreateAIPlanBlueprintMutation, useGetAIPlanBlueprintsQuery } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { RootState } from 'app/rootReducer'
import { reset, setCompanyDomain, setEmployees, setInstantPlanEnabled, setRevenue, setUserProvidedInsights } from 'features/planner/CreateAIPlanBlueprintSlice'
import { toExcelLetter } from 'utils/Formatters'

import { updateAccountState } from '../../app/appSlice'
import { ClientPicker } from '../../components/account/ClientPicker'
import history from '../../services/HistoryService'
import { OutputFormatOption, reset as resetPlan, setOutputFormat, setStep, AIPlanCreateStep } from './CreateAIPlanSlice'

const isValidUrl = (value: string): boolean => {
  if (!value) return false
  try {
    const urlWithProtocol = value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`
    const url = new URL(urlWithProtocol)
    return !!url.hostname && url.hostname.includes('.')
  } catch {
    return false
  }
}

export const AIPlanBlueprintCreatePage: React.FC = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { companyDomain, employees, revenue, userProvidedInsights, instantPlanEnabled } = useSelector((state: RootState) => state.createAIPlanBlueprint)
  const { data: blueprints } = useGetAIPlanBlueprintsQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const [beginCreateAIPlanBlueprint, beginCreateResult] = useBeginCreateAIPlanBlueprintMutation()
  const [domainTouched, setDomainTouched] = useState(false)

  useEffect(() => {
    if (!currentAccount) {
      dispatch(setCompanyDomain(''))
      return
    }
    if (currentAccount.website) {
      dispatch(setCompanyDomain(currentAccount.website))
    }
  }, [currentAccount, dispatch])

  const navigatingToInstantPlan = React.useRef(false)

  useEffect(() => {
    return () => {
      if (!navigatingToInstantPlan.current) {
        dispatch(reset())
      }
    }
  }, [dispatch])

  const onClientChange = (selectedClient: Account) => {
    dispatch(updateAccountState(selectedClient))
    if (selectedClient.website) {
      dispatch(setCompanyDomain(selectedClient.website))
    } else {
      dispatch(setCompanyDomain(''))
    }
  }

  const accountBlueprints = blueprints?.filter(b => b.accountId === currentAccount?.id) ?? []
  const accountHasBlueprints = accountBlueprints.length > 0

  const domainIsValid = isValidUrl(companyDomain)
  const isFormValid = !!currentAccount && domainIsValid

  const onSubmit = async () => {
    if (!currentOrganization || !currentAccount || !isFormValid) {
      return
    }

    const blueprintLetter = toExcelLetter(accountBlueprints.length)

    await beginCreateAIPlanBlueprint({
      organizationId: currentOrganization.id,
      params: {
        accountId: currentAccount.id,
        name: `${currentAccount.name} Blueprint ${blueprintLetter}`,
        companyName: currentAccount.name,
        companyDomain: companyDomain.trim(),
        employees,
        revenue,
        userProvidedInsights: userProvidedInsights.trim() || undefined
      }
    })

    dispatch(reset())
    history.push('/ai/blueprints')
  }

  const onContinueToInstantPlan = () => {
    navigatingToInstantPlan.current = true
    dispatch(resetPlan())
    dispatch(setOutputFormat(OutputFormatOption.OnePlan))
    dispatch(setStep(AIPlanCreateStep.OutputFormat))
    history.push('/ai/plans/create')
  }

  const onBackClick = () => {
    if (accountHasBlueprints) {
      history.push('/ai/blueprints')
    } else {
      history.push('/ai/blueprints/welcome')
    }
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>Create AI Blueprint{currentAccount ? `: ${currentAccount.name}` : ''}</h1>
            </EuiTitle>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false}>
          <EuiFormRow label='Client *' fullWidth>
            <ClientPicker selectedClient={currentAccount ?? undefined} onClientChange={onClientChange} enableNewClient />
          </EuiFormRow>
          <EuiSpacer />
          <EuiFormRow label='Brand Url *' fullWidth isInvalid={domainTouched && !domainIsValid} error={domainTouched && !domainIsValid ? 'Please enter a valid URL' : undefined}>
            <EuiFieldText fullWidth value={companyDomain} onChange={e => dispatch(setCompanyDomain(e.target.value))} onBlur={() => setDomainTouched(true)} placeholder='Enter the company website or brand URL' isInvalid={domainTouched && !domainIsValid} />
          </EuiFormRow>
          <EuiSpacer />
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiFormRow label='Number of Employees (Optional)' fullWidth>
                <EuiFieldNumber fullWidth value={employees ?? ''} onChange={e => dispatch(setEmployees(e.target.value ? Number(e.target.value) : undefined))} />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow label='Annual Revenue ($) (Optional)' fullWidth>
                <EuiFieldNumber fullWidth value={revenue ?? ''} onChange={e => dispatch(setRevenue(e.target.value ? Number(e.target.value) : undefined))} />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer />
          <EuiFormRow label='Additional Info (Optional)' fullWidth>
            <EuiTextArea fullWidth value={userProvidedInsights} onChange={e => dispatch(setUserProvidedInsights(e.target.value))} placeholder={'Additional information about the company itself. (i.e. This is a 50 year old family run business.)\n\nCampaign and marketing-related information to be added later.'} />
          </EuiFormRow>

          {isStaffUser && showInternalView && (
            <>
              <EuiHorizontalRule />
              <EuiSwitch label='Instant Plan' checked={instantPlanEnabled} onChange={e => dispatch(setInstantPlanEnabled(e.target.checked))} />
            </>
          )}

          <EuiSpacer size='xl' />
          <EuiFlexGroup alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onBackClick}>Back</EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              {instantPlanEnabled ? (
                <EuiButton fill onClick={onContinueToInstantPlan} disabled={!isFormValid}>
                  Continue to Plan Settings
                </EuiButton>
              ) : (
                <EuiButton fill onClick={onSubmit} isLoading={beginCreateResult.isLoading} disabled={!isFormValid}>
                  Create Blueprint
                </EuiButton>
              )}
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText size='xs'>
                <p>* Required fields</p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  )
}
